import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import { useState, useEffect, useRef, useCallback } from "react";
import { Table, Form, InputGroup, Button } from "react-bootstrap";
import {
  ITEMS_PER_PAGE,
  SEARCH_DEBOUNCE_TIME,
} from "../../../../constant/constant";
import StudentTableRow from "./StudentTableRow";
import TableSkeleton from "../../common/TableSkeleton";
import "../../../../common/styles/table.css";
import { useGetStudents } from "../../../../hooks/school/student/useGetStudents";
import { useDeleteStudent } from "../../../../hooks/school/student/useDeleteStudent";
import { useToggleStudentStatus } from "../../../../hooks/school/student/useToggleStudentStatus";
import TableError from "../../common/TableError";
import { useLocation } from "react-router-dom";
import RefreshButtonTable from "../../../common/RefreshButtonTable";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const StudentTable = ({ onSelectionChange, RoleGuard }) => {
  const { loading, error, students, meta, getStudents } = useGetStudents();
  const {
    deleteStudent,
    loading: deleteLoading,
    error: deleteError,
    success: deleteSuccess,
    resetState: resetDeleteState,
  } = useDeleteStudent();
  const {
    toggleStudentStatus,
    loading: toggleLoading,
    error: toggleError,
    success: toggleSuccess,
    resetState: resetToggleState,
  } = useToggleStudentStatus();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(ITEMS_PER_PAGE);
  const location = useLocation();

  // Multi-select: persisted across pagination
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState("");

  // Notify parent whenever selection changes
  useEffect(() => {
    onSelectionChange?.(selectedStudentIds);
  }, [selectedStudentIds]);

  /**
   * Build the filters object for the API call.
   */
  const buildFilters = useCallback((search, status) => {
    const filters = {};
    if (search && search.trim() !== "") {
      filters.search = search.trim();
    }
    if (status !== "") {
      filters.status = status;
    }
    return filters;
  }, []);

  /**
   * Fetch students with current page, perPage, and search filters
   */
  const fetchStudents = useCallback(
    (page, search, status, itemsPerPage) => {
      getStudents({
        page,
        per_page: itemsPerPage,
        filters: buildFilters(search, status),
      });
    },
    [buildFilters, getStudents],
  );

  // Fetch students on initial load, when search changes, or when location search changes
  useEffect(() => {
    fetchStudents(currentPage, debouncedSearch, statusFilter, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, statusFilter, perPage, location.search]);

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_TIME);
  };

  /**
   * Clear search input and reset results
   */
  const handleClearSearch = () => {
    setSearchTerm("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setCurrentPage(1);
    setDebouncedSearch("");
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    fetchStudents(currentPage, debouncedSearch, statusFilter, perPage);
  };

  /**
   * Handle status filter change
   */
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  /**
   * Handle student deletion
   */
  const handleDeleteStudent = async (studentId) => {
    await deleteStudent(studentId);
  };

  /**
   * Handle successful deletion - refresh the list
   */
  const handleDeleteSuccess = () => {
    if (students && students.length === 1 && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    } else {
      handleRefresh();
    }
  };

  /**
   * Handle student toggle
   */
  const handleToggleStudent = async (studentId) => {
    await toggleStudentStatus(studentId);
  };

  /**
   * Handle successful toggle - refresh the list
   */
  const handleToggleSuccess = () => {
    handleRefresh();
  };

  // --- Multi-select handlers ---
  const handleToggleSelect = (studentId) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const handleSelectAllOnPage = (checked) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        students?.forEach((s) => next.add(s.id));
      } else {
        students?.forEach((s) => next.delete(s.id));
      }
      return next;
    });
  };

  const allOnPageSelected =
    students &&
    students.length > 0 &&
    students.every((s) => selectedStudentIds.has(s.id));

  // --- Per-page handler ---
  const handlePerPageChange = (e) => {
    const newPerPage = Number(e.target.value);
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Show skeleton only on initial load (when students is null and loading)
  if (loading && students === null) {
    return <TableSkeleton rows={ITEMS_PER_PAGE} columns={4} />;
  }

  if (error) {
    return (
      <TableError
        error={error}
        handleRefresh={handleRefresh}
        loading={loading}
      />
    );
  }

  const totalPages = meta?.last_page || 1;
  const hasStudents = students && students.length > 0;
  const isSearching = debouncedSearch.trim() !== "";

  return (
    <div>
      {/* Search and Refresh Row */}
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div className="d-flex gap-2 flex-wrap flex-grow-1">
          <InputGroup style={{ maxWidth: "400px" }}>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name or email or student id..."
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading && !isSearching && students === null}
              className="shadow-none"
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={handleClearSearch}
                disabled={loading}
                title="Clear search"
              >
                <i className="bi bi-x-lg"></i>
              </Button>
            )}
          </InputGroup>

          <Form.Select
            value={statusFilter}
            onChange={handleStatusChange}
            disabled={loading && students === null}
            style={{ maxWidth: "200px" }}
            className="shadow-none"
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </Form.Select>
        </div>

        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {!hasStudents ? (
        <div className="alert alert-info mt-3" role="alert">
          {isSearching
            ? `No students found matching "${debouncedSearch}".`
            : "No students found."}
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <RoleGuard permission="dashboard_assign_teachers">
                    <th style={{ width: "40px" }}>
                      <Form.Check
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={(e) =>
                          handleSelectAllOnPage(e.target.checked)
                        }
                        aria-label="Select all students on page"
                      />
                    </th>
                  </RoleGuard>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Assigned Teacher</th>
                  <RoleGuard permission="students_edit">
                    <th>Status</th>
                  </RoleGuard>

                  <RoleGuard permission="students_action">
                    <th>Action</th>
                  </RoleGuard>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => {
                  return (
                    <StudentTableRow
                      key={student.id}
                      student={student}
                      isSelected={selectedStudentIds.has(student.id)}
                      onToggleSelect={handleToggleSelect}
                      onDelete={handleDeleteStudent}
                      deleteLoading={deleteLoading}
                      deleteError={deleteError}
                      deleteSuccess={deleteSuccess}
                      resetDeleteState={resetDeleteState}
                      onDeleteSuccess={handleDeleteSuccess}
                      onToggle={handleToggleStudent}
                      toggleLoading={toggleLoading}
                      toggleError={toggleError}
                      toggleSuccess={toggleSuccess}
                      resetToggleState={resetToggleState}
                      onToggleSuccess={handleToggleSuccess}
                      RoleGuard={RoleGuard}
                    />
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Pagination + Items per page */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-2">
            <TablePagination
              totalPage={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              disabled={loading}
            />

            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "14px", whiteSpace: "nowrap" }}>
                Rows per page:
              </span>
              <Form.Select
                size="sm"
                value={perPage}
                onChange={handlePerPageChange}
                style={{ width: "auto" }}
                disabled={loading}
              >
                {PER_PAGE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTable;
